(() => {
  const init = (root) => {
    if (!root || root.dataset.hpv3Ready === 'true') return;
    root.dataset.hpv3Ready = 'true';

    const gallery = root.querySelector('[data-hpv3-gallery]');
    const buybox = root.querySelector('[data-hpv3-buybox]');

    /* =========================================================
       GALLERY
       ========================================================= */
    if (gallery) {
      const slides = Array.from(gallery.querySelectorAll('[data-media-slide]'));
      const thumbs = Array.from(gallery.querySelectorAll('[data-thumb]'));
      const progressDots = Array.from(gallery.querySelectorAll('[data-progress-dot]'));
      const mobileDots = Array.from(gallery.querySelectorAll('[data-mobile-dot]'));
      const stage = gallery.querySelector('[data-media-stage]');
      const thumbViewport = gallery.querySelector('[data-thumb-viewport]');
      const thumbUp = gallery.querySelector('[data-thumb-up]');
      const thumbDown = gallery.querySelector('[data-thumb-down]');
      const progressHandle = gallery.querySelector('[data-gallery-progress]');
      const zoomButton = gallery.querySelector('[data-gallery-zoom]');

      const lightbox = gallery.querySelector('[data-gallery-lightbox]');
      const lightboxImage = gallery.querySelector('[data-lightbox-image]');
      const lightboxPrev = gallery.querySelector('[data-lightbox-prev]');
      const lightboxNext = gallery.querySelector('[data-lightbox-next]');
      const lightboxClose = Array.from(gallery.querySelectorAll('[data-lightbox-close]'));

      if (lightbox && lightbox.parentElement !== document.body) {
        document.body.appendChild(lightbox);
      }

      let activeIndex = 0;
      let wheelLock = false;

      const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

      const updateProgress = () => {
        if (!progressHandle || slides.length <= 1) return;
        const rail = progressHandle.parentElement;
        if (!rail) return;

        const railHeight = rail.clientHeight - 76;
        const handleHeight = progressHandle.offsetHeight;
        const travel = Math.max(0, railHeight - handleHeight);

        let ratio = activeIndex / Math.max(1, slides.length - 1);

        if (stage && window.matchMedia('(min-width: 990px)').matches) {
          const maxScroll = Math.max(1, stage.scrollHeight - stage.clientHeight);
          ratio = stage.scrollTop / maxScroll;
        }

        progressHandle.style.top = `${38 + (travel * Math.max(0, Math.min(1, ratio)))}px`;
      };

      const ensureThumbVisible = () => {
        const thumb = thumbs[activeIndex];
        if (!thumb || !thumbViewport) return;

        const t = thumb.getBoundingClientRect();
        const v = thumbViewport.getBoundingClientRect();

        if (t.top < v.top) {
          thumbViewport.scrollBy({ top: t.top - v.top - 8, behavior: 'smooth' });
        } else if (t.bottom > v.bottom) {
          thumbViewport.scrollBy({ top: t.bottom - v.bottom + 8, behavior: 'smooth' });
        }
      };

      const pauseInactiveVideos = () => {
        slides.forEach((slide, index) => {
          if (index === activeIndex) return;
          slide.querySelectorAll('video').forEach((video) => video.pause());
        });
      };

      const setActive = (index, opts = {}) => {
        if (!slides.length) return;
        activeIndex = clamp(index, 0, slides.length - 1);

        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === activeIndex);
        });

        thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === activeIndex);
          thumb.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
        });

        progressDots.forEach((dot, i) => dot.classList.toggle('is-active', i === activeIndex));
        mobileDots.forEach((dot, i) => dot.classList.toggle('is-active', i === activeIndex));

        updateProgress();
        ensureThumbVisible();
        pauseInactiveVideos();

        if (opts.scrollMobile && stage && window.matchMedia('(max-width: 989px)').matches) {
          stage.scrollTo({
            left: stage.clientWidth * activeIndex,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        }
      };

      const scrollDesktopToIndex = (index) => {
        if (!stage) return;
        const safeIndex = clamp(index, 0, slides.length - 1);
        const targetSlide = slides[safeIndex];
        if (!targetSlide) return;

        stage.scrollTo({
          top: targetSlide.offsetTop,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      };

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const index = Number(thumb.dataset.mediaIndex || 0);

          if (window.matchMedia('(min-width: 990px)').matches) {
            scrollDesktopToIndex(index);
          } else {
            setActive(index, { scrollMobile: true });
          }
        });
      });

      mobileDots.forEach((dot) => {
        dot.addEventListener('click', () => {
          setActive(Number(dot.dataset.mediaIndex || 0), { scrollMobile: true });
        });
      });

      thumbUp?.addEventListener('click', () => {
        if (window.matchMedia('(min-width: 990px)').matches) {
          scrollDesktopToIndex(activeIndex - 1);
        } else {
          setActive(activeIndex - 1, { scrollMobile: true });
        }
      });

      thumbDown?.addEventListener('click', () => {
        if (window.matchMedia('(min-width: 990px)').matches) {
          scrollDesktopToIndex(activeIndex + 1);
        } else {
          setActive(activeIndex + 1, { scrollMobile: true });
        }
      });

      /* Desktop uses native continuous scrolling inside the media stage. */

      if (stage) {
        let rafId = null;

        stage.addEventListener('scroll', () => {
          cancelAnimationFrame(rafId);

          rafId = requestAnimationFrame(() => {
            if (window.matchMedia('(min-width: 990px)').matches) {
              const viewportCenter = stage.scrollTop + (stage.clientHeight / 2);
              let index = 0;
              let closestDistance = Number.POSITIVE_INFINITY;

              slides.forEach((slide, i) => {
                const slideCenter = slide.offsetTop + (slide.offsetHeight / 2);
                const distance = Math.abs(slideCenter - viewportCenter);

                if (distance < closestDistance) {
                  closestDistance = distance;
                  index = i;
                }
              });

              index = clamp(index, 0, slides.length - 1);

              if (index !== activeIndex) {
                activeIndex = index;

                slides.forEach((slide, i) => slide.classList.toggle('is-active', i === activeIndex));

                thumbs.forEach((thumb, i) => {
                  thumb.classList.toggle('is-active', i === activeIndex);
                  thumb.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
                });

                progressDots.forEach((dot, i) => dot.classList.toggle('is-active', i === activeIndex));
                mobileDots.forEach((dot, i) => dot.classList.toggle('is-active', i === activeIndex));

                ensureThumbVisible();
                pauseInactiveVideos();
              }

              updateProgress();
            } else {
              const index = Math.round(stage.scrollLeft / Math.max(1, stage.clientWidth));
              setActive(index);
            }
          });
        }, { passive: true });
      }

      /* -------- Lightbox -------- */
      const imageSlideIndexes = slides
        .map((slide, index) => slide.dataset.mediaType === 'image' ? index : null)
        .filter((index) => index !== null);

      const nearestImageIndex = (fromIndex, direction = 1) => {
        if (!imageSlideIndexes.length) return null;

        let cursor = imageSlideIndexes.indexOf(fromIndex);

        if (cursor === -1) {
          cursor = imageSlideIndexes.findIndex((index) => index > fromIndex);
          if (cursor === -1) cursor = 0;
        } else {
          cursor = (cursor + direction + imageSlideIndexes.length) % imageSlideIndexes.length;
        }

        return imageSlideIndexes[cursor];
      };

      const syncLightbox = (index = activeIndex) => {
        const slide = slides[index];
        if (!slide || slide.dataset.mediaType !== 'image' || !lightboxImage) return false;

        lightboxImage.src = slide.dataset.fullSrc || slide.querySelector('img')?.currentSrc || slide.querySelector('img')?.src || '';
        lightboxImage.alt = slide.querySelector('img')?.alt || '';
        return true;
      };

      const openLightbox = () => {
        if (!lightbox) return;

        let index = activeIndex;
        if (slides[index]?.dataset.mediaType !== 'image') {
          index = nearestImageIndex(activeIndex, 1);
          if (index === null) return;
          setActive(index);
        }

        if (!syncLightbox(index)) return;

        lightbox.classList.add('is-open');
        lightbox.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      };

      const closeLightbox = () => {
        if (!lightbox) return;
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      };

      zoomButton?.addEventListener('click', openLightbox);

      slides.forEach((slide) => {
        const image = slide.querySelector('.hpv3-gallery__image');
        image?.addEventListener('click', openLightbox);
      });

      lightboxPrev?.addEventListener('click', () => {
        const next = nearestImageIndex(activeIndex, -1);
        if (next === null) return;
        setActive(next);
        syncLightbox(next);
      });

      lightboxNext?.addEventListener('click', () => {
        const next = nearestImageIndex(activeIndex, 1);
        if (next === null) return;
        setActive(next);
        syncLightbox(next);
      });

      lightboxClose.forEach((button) => button.addEventListener('click', closeLightbox));

      window.addEventListener('resize', updateProgress);
      setActive(0);

      root._hpv3CloseLightbox = closeLightbox;
    }


    /* =========================================================
       DELIVERY / RETURNS INFO MODALS
       ========================================================= */
    const infoModals = Array.from(root.querySelectorAll('[data-hpv3-info-modal]'));
    const infoOpeners = Array.from(root.querySelectorAll('[data-hpv3-info-open]'));
    let lastInfoTrigger = null;

    const closeInfoModal = (modal, returnFocus = true) => {
      if (!modal) return;

      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');

      if (!root.querySelector('.hpv3-info-modal.is-open')) {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }

      if (returnFocus) lastInfoTrigger?.focus?.();
    };

    const openInfoModal = (key, trigger = null) => {
      const modal = infoModals.find((item) => item.dataset.hpv3InfoModal === key);
      if (!modal) return;

      lastInfoTrigger = trigger || document.activeElement;

      infoModals.forEach((item) => {
        item.classList.remove('is-open');
        item.setAttribute('aria-hidden', 'true');
      });

      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }

      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      window.requestAnimationFrame(() => {
        modal.querySelector('.hpv3-info-modal__dialog')?.focus();
      });
    };

    infoOpeners.forEach((button) => {
      button.addEventListener('click', () => {
        openInfoModal(button.dataset.hpv3InfoOpen, button);
      });
    });

    infoModals.forEach((modal) => {
      modal.querySelectorAll('[data-hpv3-info-close]').forEach((button) => {
        button.addEventListener('click', () => closeInfoModal(modal));
      });
    });

    /* =========================================================
       BUY BOX / VARIANTS
       ========================================================= */
    if (buybox) {
      /* Native browser scroll chaining handles buybox-to-page handoff. */
      const form = buybox.querySelector('.hpv3-buybox__form');
      const variantInput = buybox.querySelector('[data-variant-input]');
      const addButton = buybox.querySelector('[data-add-to-cart]');
      const addLabel = buybox.querySelector('[data-add-label]');
      const priceEl = buybox.querySelector('[data-product-price]');
      const compareEl = buybox.querySelector('[data-compare-price]');
      const afterpayAmountEl = buybox.querySelector('[data-afterpay-amount]');
      const status = buybox.querySelector('[data-product-status]');
      const sizeButtons = Array.from(buybox.querySelectorAll('[data-size-value]'));
      const variantsJson = buybox.querySelector('[data-product-variants-json]');
      const swatchLabel = buybox.querySelector('[data-swatch-label-text]');
      const swatches = Array.from(buybox.querySelectorAll('[data-swatch-label]'));

      let variants = [];
      try {
        variants = JSON.parse(variantsJson?.textContent || '[]');
      } catch (e) {
        variants = [];
      }

      const money = (cents) => {
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);

        return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'AUD'
        }).format((Number(cents || 0)) / 100);
      };

      const sizeOptionIndex = (() => {
        const selected = sizeButtons[0]?.dataset.sizeValue;
        if (!variants.length || !selected) return -1;

        return [0, 1, 2].find((index) =>
          variants.some((variant) => variant[`option${index + 1}`] === selected)
        ) ?? -1;
      })();

      const updateVariant = (sizeValue) => {
        if (sizeOptionIndex < 0) return;

        const optionKey = `option${sizeOptionIndex + 1}`;
        const variant = variants.find((v) => v[optionKey] === sizeValue && v.available)
          || variants.find((v) => v[optionKey] === sizeValue);

        if (!variant) return;

        if (variantInput) variantInput.value = variant.id;

        sizeButtons.forEach((button) => {
          button.classList.toggle('is-selected', button.dataset.sizeValue === sizeValue);
        });

        if (priceEl) priceEl.textContent = money(variant.price);
        if (afterpayAmountEl) afterpayAmountEl.textContent = money(Math.round(Number(variant.price || 0) / 4));

        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = money(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        if (addButton) addButton.disabled = !variant.available;
        if (addLabel) addLabel.textContent = variant.available ? `Add to Cart — ${money(variant.price)}` : 'Sold Out';
        if (status) status.textContent = '';

        root.dispatchEvent(new CustomEvent('harpah:variant-change', {
          bubbles: true,
          detail: { variant }
        }));
      };

      sizeButtons.forEach((button) => {
        button.addEventListener('click', () => updateVariant(button.dataset.sizeValue));
      });

      /* Swatch label preview copied from the behaviour of the old HARPAH product section. */
      if (swatchLabel && swatches.length) {
        const original = swatchLabel.textContent;

        swatches.forEach((swatch) => {
          swatch.addEventListener('mouseenter', () => {
            if (swatch.dataset.swatchLabel) swatchLabel.textContent = swatch.dataset.swatchLabel;
          });

          swatch.addEventListener('mouseleave', () => {
            swatchLabel.textContent = original;
          });
        });
      }

      form?.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (!variantInput?.value || addButton?.disabled) {
          if (status) status.textContent = 'Please select an available size.';
          return;
        }

        const original = addLabel?.textContent || 'Add to Bag';
        if (addButton) addButton.disabled = true;
        if (addLabel) addLabel.textContent = 'Adding…';
        if (status) status.textContent = '';

        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: [{ id: Number(variantInput.value), quantity: 1 }]
            })
          });

          const data = await response.json().catch(() => ({}));

          if (!response.ok) {
            throw new Error(data.description || data.message || 'This item could not be added.');
          }

          if (addLabel) addLabel.textContent = 'Added ✓';

          /* Cart Drawer V3 Stable
             Do not fire competing refresh/open events here.
             The global Cart Drawer V3 click listener captures this Add to Bag click,
             checks that cart.item_count actually increased, then refreshes and opens
             the drawer once with fresh cart HTML. This also covers the Sticky Bar,
             which delegates to this same PDP button. */
          window.dispatchEvent(new CustomEvent('harpah:cart-added', { detail: data }));

          window.setTimeout(() => {
            if (addLabel) addLabel.textContent = original;
            if (addButton) addButton.disabled = false;
          }, 1400);
        } catch (error) {
          if (status) status.textContent = error?.message || 'Something went wrong. Please try again.';
          if (addLabel) addLabel.textContent = original;
          if (addButton) addButton.disabled = false;
        }
      });

      /* -------- Community Feedback -------- */
      buybox.querySelector('[data-scroll-community-feedback]')?.addEventListener('click', () => {
        const reviews = document.querySelector('[data-harpah-reviews]');
        if (!reviews) return;

        reviews.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
      });

      /* -------- KiwiSizing -------- */
      const kiwiTriggers = Array.from(buybox.querySelectorAll('[data-size-guide-trigger]'));
      const configuredSelector = root.dataset.kiwiSelector?.trim() || '.ks-chart-modal-link.sizing-chart-modal-link';

      const findKiwiNativeTrigger = () => {
        const selectors = [
          configuredSelector,
          '.ks-chart-modal-link.sizing-chart-modal-link',
          '.ks-chart-modal-link',
          '.sizing-chart-modal-link',
          '.kiwi-sizing-modal-link',
          '.kiwiSizingChart',
          '.ks-sizing-modal-link'
        ].filter(Boolean);

        for (const selector of selectors) {
          try {
            const candidates = Array.from(document.querySelectorAll(selector));
            const native = candidates.find((node) => !node.matches('[data-size-guide-trigger]'));
            if (native) return native;
          } catch (e) {}
        }

        return null;
      };

      const openKiwi = (attempt = 0) => {
        const native = findKiwiNativeTrigger();

        if (native) {
          native.click();
          return;
        }

        if (attempt < 12) {
          window.setTimeout(() => openKiwi(attempt + 1), 150);
        }
      };

      kiwiTriggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          openKiwi();
        });
      });
    }

    /* =========================================================
       MODEL FIT DRAWER
       ========================================================= */
    const modelOpen = root.querySelector('[data-model-fit-open]');
    const modelDrawer = root.querySelector('[data-model-fit-drawer]');
    const modelClose = Array.from(root.querySelectorAll('[data-model-fit-close]'));

    const openModelDrawer = () => {
      if (!modelDrawer) return;
      modelDrawer.classList.add('is-open');
      modelDrawer.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
    };

    const closeModelDrawer = () => {
      if (!modelDrawer) return;
      modelDrawer.classList.remove('is-open');
      modelDrawer.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
    };

    modelOpen?.addEventListener('click', openModelDrawer);
    modelClose.forEach((el) => el.addEventListener('click', closeModelDrawer));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const openInfo = document.querySelector('.hpv3-info-modal.is-open');
        if (openInfo) {
          closeInfoModal(openInfo);
          return;
        }
      }

      if (event.key !== 'Escape') return;

      if (modelDrawer?.classList.contains('is-open')) closeModelDrawer();
      root._hpv3CloseLightbox?.();
    });
  };

  const boot = () => {
    document.querySelectorAll('[data-hpv3-main]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
window.addEventListener('pageshow', () => {
  document.querySelectorAll('video[preload="none"]').forEach((video) => {
    if (video.closest('.hpv3-gallery__slide.is-active')) {
      video.preload = 'metadata';
    }
  });
}, { passive: true });

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;

    const modal = document.querySelector(`.hpv3-info-modal[data-block-id="${blockId}"]`);
    if (!modal) return;

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const blockId = event.detail?.blockId;
    const modal = document.querySelector(`.hpv3-info-modal[data-block-id="${blockId}"]`);
    if (!modal) return;

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  });
}
